import React from "react";
import type { match } from "react-router-dom";
import { useHistory, Link } from "react-router-dom";
import { WebhookType } from "../../../../models/LogFilter";
import IntegrationNameAndLogo from "../../../shared/IntegrationNameAndLogo";
import { HTTPMethodBadge, ResponseCodeBadge } from "../../../shared/MergeBadges";
import type { WebhookLogEntry } from "../../IntegrationsManagementEntities";
import DirectionCell from "../shared/DirectionCell";
import { LinkedAccount } from "../../../../models/Entities";
import { formatDate } from "../../../../models/Helpers";
import { Tooltip, Text } from "@merge-api/merge-javascript-shared";
import { ChevronRight } from "lucide-react";

interface Props {
  log: WebhookLogEntry;
  match: match;
  linkedAccount?: LinkedAccount | null;

  /**
   * Called when the route is pushed when clicking this row
   */
  onPush?: () => void;
}

const WebhookLogEntryLink = (props: React.PropsWithChildren<{ logId: string; match: match }>) => {
  return (
    <Link
      onClick={(e) => e.stopPropagation()}
      to={`${props.match.url}/${props.logId}`}
      className="block w-full text-black"
    >
      {props.children}
    </Link>
  );
};

/**
 * Creates one table row for a log entry
 */
const WebhookLogEntryRow = ({ log, match, onPush, linkedAccount }: Props) => {
  const history = useHistory();

  const navigateToLogEntrySidePanel = (logId: string) => {
    onPush && onPush();
    history.push(`${match.url}/${logId}`, { from: window.location.pathname });
  };

  const methodCell = (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <HTTPMethodBadge method={log?.method ?? "POST"} />
      </WebhookLogEntryLink>
    </td>
  );

  // URL column absorbs the table's leftover horizontal space (w-full + max-w-0)
  // so it uses whatever room the screen gives it, with a min-width floor so it
  // never collapses to nothing on a crowded row.
  // URL cell contains tenant-scoped webhook URLs and is thus masked at the cell
  // (Tooltip body is portal-mounted and covered by maskTextSelector).
  const urlCell = (
    <td
      className="ph-no-capture w-full min-w-[50px] max-w-0 md:min-w-[100px]"
      onClick={() => navigateToLogEntrySidePanel(log.id)}
    >
      <WebhookLogEntryLink logId={log.id} match={match}>
        {log.url && (
          <Tooltip title={log.url} className="flex w-full min-w-0 items-center truncate">
            <Text className="min-w-0 truncate">{log.url}</Text>
          </Tooltip>
        )}
      </WebhookLogEntryLink>
    </td>
  );

  const directionCell = log?.direction ? (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <DirectionCell log={log} />
      </WebhookLogEntryLink>
    </td>
  ) : null;

  const statusCell = (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <ResponseCodeBadge responseCode={log.response_code} />
      </WebhookLogEntryLink>
    </td>
  );

  const typeCell = log?.webhook?.type ? (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <Text>
          {log?.webhook.type === WebhookType.CHANGED_DATA
            ? "Changed data"
            : log?.webhook?.type === WebhookType.SYNC_NOTIFICATION
            ? "Sync notification"
            : log?.webhook?.type === WebhookType.RECEIVING_WEBHOOK
            ? "Receiving webhook"
            : log?.webhook.type === WebhookType.LINKED_ACCOUNT_ISSUE
            ? "Linked Account issue"
            : log?.webhook?.type === WebhookType.ASYNC_POST
            ? "Async post"
            : log?.webhook?.type === WebhookType.ASYNC_BULK_POST
            ? "Async bulk post"
            : ""}
        </Text>
      </WebhookLogEntryLink>
    </td>
  ) : null;

  const eventCell = log?.webhook?.event ? (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <Text>{log?.webhook?.event}</Text>
      </WebhookLogEntryLink>
    </td>
  ) : null;

  const integrationCell = !linkedAccount ? (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        {log.linked_account?.integration && (
          <IntegrationNameAndLogo integration={log.linked_account.integration} />
        )}
      </WebhookLogEntryLink>
    </td>
  ) : null;

  const userCell = !linkedAccount ? (
    // Organization name cell is customer-chosen and is thus masked at the cell
    <td className="ph-no-capture" onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <Text>{log.linked_account?.end_user?.organization_name}</Text>
      </WebhookLogEntryLink>
    </td>
  ) : null;

  const dateCell = (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <Text className="text-gray-40">{formatDate(log.created_at)}</Text>
      </WebhookLogEntryLink>
    </td>
  );

  const chevronCell = (
    <td className="text-right" onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <WebhookLogEntryLink logId={log.id} match={match}>
        <Tooltip title="Review log details">
          <ChevronRight size={16} className="text-gray-40" />
        </Tooltip>
      </WebhookLogEntryLink>
    </td>
  );

  return (
    <tr className="table-link">
      {methodCell}
      {urlCell}
      {directionCell}
      {statusCell}
      {typeCell}
      {eventCell}
      {integrationCell}
      {userCell}
      {dateCell}
      {chevronCell}
    </tr>
  );
};

export default WebhookLogEntryRow;
